import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { norm, euro, request, openPrint } from './StockAdmin'
import { parseAllergens } from './productFields'

// Jednopísmenové slovenské predložky/spojky („s malinami“, „k narodeninám“...) sa pri zalomení názvu
// nesmú odtrhnúť od nasledujúceho slova – rovnaká logika ako na serveri v src/price-tags.ts.
function nbspOrphans(text: string): string {
  return text.replace(/\b([aiksvzouAIKSVZOU])[ \t]+(?=\S)/g, (_m, w: string) => `${w} `)
}

/** Tlač cenoviek – výber produktov (s filtrom podľa e-shop skupiny), polí, veľkosti cenovky a formátu (A4 hárok s čiarami na strihanie, alebo štítková tlačiareň). */

type Product = { id: string; name: string; unit: string; priceCents: number; weightGrams?: number | null; volumeMl?: number | null; allergens?: string | null; glutenFree?: boolean; lactoseFree?: boolean; active: boolean; plu?: string | null; category: string; shopCategories?: Array<{ categoryId: string }> }
type Category = { id: string; name: string; parentId: string | null; sortOrder: number }
type Field = 'weight' | 'unitPrice' | 'allergens'
type SizeKey = 'S' | 'M' | 'L' | 'CUSTOM'
type Align = 'left' | 'center' | 'right'
type Settings = { fields: Field[]; size: SizeKey; customWidthMm: number; customHeightMm: number; format: 'A4' | 'LABEL'; nameScale: number; priceScale: number; detailScale: number; align: Align; color: string; showUnitLabel: boolean; nameBold: boolean }
type SizeInfo = { label: string; widthMm: number; heightMm: number }
type Sort = 'name' | 'price-asc' | 'price-desc'
type Preset = { id: string; name: string; items: Array<{ productId: string; count: number }>; updatedAt: string }
const ALIGN_OPTIONS: Array<[Align, string]> = [['left', '⇤ Vľavo'], ['center', '↔ Na stred'], ['right', '⇥ Vpravo']]
const ALIGN_CSS: Record<Align, 'left' | 'center' | 'right' | 'flex-start' | 'flex-end'> = { left: 'flex-start', center: 'center', right: 'flex-end' }

const unitPricePer100g = (p: Pick<Product, 'priceCents' | 'unit' | 'weightGrams'>) => {
  if (p.weightGrams && p.weightGrams > 0) return Math.round((p.priceCents / p.weightGrams) * 100)
  if (p.unit === 'kg') return Math.round(p.priceCents / 10)
  return null
}
const unitPricePer100ml = (p: Pick<Product, 'priceCents' | 'unit' | 'volumeMl'>) => {
  if (p.volumeMl && p.volumeMl > 0) return Math.round((p.priceCents / p.volumeMl) * 100)
  if (p.unit === 'l') return Math.round(p.priceCents / 10)
  return null
}
/** Jednotková cena za 100 g alebo 100 ml – podľa toho, čo je pri produkte vyplnené (rovnaká logika ako na serveri). */
const unitPriceForTag = (p: Pick<Product, 'priceCents' | 'unit' | 'weightGrams' | 'volumeMl'>): { cents: number; per: '100 g' | '100 ml' } | null => {
  if (p.weightGrams && p.weightGrams > 0) return { cents: unitPricePer100g(p)!, per: '100 g' }
  if (p.volumeMl && p.volumeMl > 0) return { cents: unitPricePer100ml(p)!, per: '100 ml' }
  if (p.unit === 'kg') return { cents: unitPricePer100g(p)!, per: '100 g' }
  if (p.unit === 'l') return { cents: unitPricePer100ml(p)!, per: '100 ml' }
  return null
}

export default function PriceTagsAdmin({ products, categories }: { products: Product[]; categories: Category[] }) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [sizes, setSizes] = useState<Record<string, SizeInfo>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [groupId, setGroupId] = useState('')
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [sort, setSort] = useState<Sort>('name')
  const [view, setView] = useState<'list' | 'grid'>('list')
  const [presets, setPresets] = useState<Preset[]>([])
  const [presetName, setPresetName] = useState('')
  const [presetBusy, setPresetBusy] = useState(false)
  const [presetError, setPresetError] = useState('')

  useEffect(() => {
    request('/api/admin/price-tag-settings').then(data => { setSettings(data.settings); setSizes(data.sizes) }).catch(() => setError('Nastavenie cenoviek sa nepodarilo načítať.'))
    request('/api/admin/price-tag-presets').then(data => setPresets(Array.isArray(data.presets) ? data.presets : [])).catch(() => {})
  }, [])

  const [includeSubs, setIncludeSubs] = useState(() => { try { return localStorage.getItem('emili-pricetags-subcats') !== '0' } catch { return true } })
  const toggleIncludeSubs = () => { setIncludeSubs(v => { const next = !v; try { localStorage.setItem('emili-pricetags-subcats', next ? '1' : '0') } catch { /* nevadí */ } return next }) }
  const topGroups = useMemo(() => categories.filter(c => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder), [categories])
  const inGroup = (p: Product) => !groupId || (p.shopCategories ?? []).some(sc => sc.categoryId === groupId || (includeSubs && categories.find(c => c.id === sc.categoryId)?.parentId === groupId))
  const q = norm(query.trim())
  const results = useMemo(() => products.filter(p => p.active && inGroup(p) && (!q || norm(p.name).includes(q) || (p.plu ?? '').includes(q)))
    .sort((a, b) => sort === 'price-asc' ? a.priceCents - b.priceCents : sort === 'price-desc' ? b.priceCents - a.priceCents : a.name.localeCompare(b.name, 'sk')), [products, q, groupId, categories, sort, includeSubs])
  const selectedIds = Object.keys(counts).filter(id => counts[id] > 0)
  const totalTags = selectedIds.reduce((sum, id) => sum + counts[id], 0)
  const allResultsSelected = results.length > 0 && results.every(p => (counts[p.id] ?? 0) > 0)

  const setCount = (id: string, value: number) => setCounts(current => ({ ...current, [id]: Math.max(0, Math.min(50, value)) }))
  const clearSelection = () => setCounts({})
  const toggleAllResults = () => setCounts(current => {
    if (allResultsSelected) { const next = { ...current }; results.forEach(p => { delete next[p.id] }); return next }
    const next = { ...current }; results.forEach(p => { if (!(next[p.id] > 0)) next[p.id] = 1 }); return next
  })

  async function savePreset() {
    if (!presetName.trim() || !selectedIds.length) return
    setPresetBusy(true); setPresetError('')
    try {
      const data = await request('/api/admin/price-tag-presets', { method: 'POST', body: JSON.stringify({ name: presetName.trim(), items: selectedIds.map(id => ({ productId: id, count: counts[id] })) }) })
      setPresets(current => [...current, data.preset])
      setPresetName('')
    } catch (e) { setPresetError(e instanceof Error ? e.message : 'Zostavu sa nepodarilo uložiť.') }
    finally { setPresetBusy(false) }
  }

  function loadPreset(preset: Preset) {
    const next: Record<string, number> = {}
    preset.items.forEach(item => { next[item.productId] = item.count })
    setCounts(next)
  }

  async function deletePreset(id: string) {
    setPresetBusy(true); setPresetError('')
    try { await request(`/api/admin/price-tag-presets/${id}`, { method: 'DELETE' }); setPresets(current => current.filter(p => p.id !== id)) }
    catch (e) { setPresetError(e instanceof Error ? e.message : 'Zostavu sa nepodarilo zmazať.') }
    finally { setPresetBusy(false) }
  }

  async function saveSettings(next: Settings) {
    setSettings(next); setSaving(true); setError('')
    try { await request('/api/admin/price-tag-settings', { method: 'PUT', body: JSON.stringify(next) }) }
    catch (e) { setError(e instanceof Error ? e.message : 'Nastavenie sa nepodarilo uložiť.') }
    finally { setSaving(false) }
  }

  function printTags() {
    if (!selectedIds.length || !settings) return
    const items = selectedIds.map(id => `${id}:${counts[id]}`).join(',')
    openPrint(`/api/admin/price-tags-print?${new URLSearchParams({ items, size: settings.size, customWidthMm: String(settings.customWidthMm), customHeightMm: String(settings.customHeightMm), format: settings.format, fields: settings.fields.join(','), nameScale: String(settings.nameScale), priceScale: String(settings.priceScale), detailScale: String(settings.detailScale), align: settings.align, color: settings.color, showUnitLabel: settings.showUnitLabel ? '1' : '0', nameBold: settings.nameBold ? '1' : '0' })}`, `Cenovky (${totalTags})`)
  }

  if (!settings) return <div className="panel"><p className="empty-message">{error || 'Načítavam…'}</p></div>

  const sample = selectedIds.length ? products.find(x => x.id === selectedIds[0]) : undefined
  const sampleUnitPrice = sample ? unitPriceForTag(sample) : null

  return <div className="panel pricetags-admin">
    <div className="panel-heading"><div><h2>Cenovky</h2><p>Vyberte produkty a vytlačte cenovky na pult – polia, veľkosť aj formát si nastavíte podľa tlačiarne.</p></div></div>

    <section className="pt-settings">
      <div className="pt-settings-group">
        <h3>Čo sa zobrazí</h3>
        <p className="pt-hint">Názov a cena s DPH sú na cenovke vždy.</p>
        <label className="pt-check"><input type="checkbox" checked={settings.fields.includes('weight')} onChange={e => void saveSettings({ ...settings, fields: e.target.checked ? [...settings.fields, 'weight'] : settings.fields.filter(f => f !== 'weight') })} /> Hmotnosť / objem</label>
        <label className="pt-check"><input type="checkbox" checked={settings.fields.includes('unitPrice')} onChange={e => void saveSettings({ ...settings, fields: e.target.checked ? [...settings.fields, 'unitPrice'] : settings.fields.filter(f => f !== 'unitPrice') })} /> Jednotková cena (100 g / 100 ml)</label>
        <label className="pt-check"><input type="checkbox" checked={settings.fields.includes('allergens')} onChange={e => void saveSettings({ ...settings, fields: e.target.checked ? [...settings.fields, 'allergens'] : settings.fields.filter(f => f !== 'allergens') })} /> Alergény</label>
      </div>
      <div className="pt-settings-group">
        <h3>Veľkosť cenovky</h3>
        <div className="pt-size-options">
          {Object.entries(sizes).map(([key, info]) => <button key={key} type="button" className={`pt-size-btn ${settings.size === key ? 'is-on' : ''}`} onClick={() => void saveSettings({ ...settings, size: key as SizeKey })}>{info.label}</button>)}
          <button type="button" className={`pt-size-btn ${settings.size === 'CUSTOM' ? 'is-on' : ''}`} onClick={() => void saveSettings({ ...settings, size: 'CUSTOM' })}>Vlastná</button>
        </div>
        {settings.size === 'CUSTOM' && <div className="pt-custom-size">
          <label>Šírka (mm)<input type="number" min={20} max={200} value={settings.customWidthMm} onChange={e => void saveSettings({ ...settings, customWidthMm: Number(e.target.value) || settings.customWidthMm })} /></label>
          <label>Výška (mm)<input type="number" min={15} max={150} value={settings.customHeightMm} onChange={e => void saveSettings({ ...settings, customHeightMm: Number(e.target.value) || settings.customHeightMm })} /></label>
        </div>}
      </div>
      <div className="pt-settings-group">
        <h3>Formát tlače</h3>
        <div className="pt-size-options">
          <button type="button" className={`pt-size-btn ${settings.format === 'A4' ? 'is-on' : ''}`} onClick={() => void saveSettings({ ...settings, format: 'A4' })}>Hárok A4 (čiary na strihanie)</button>
          <button type="button" className={`pt-size-btn ${settings.format === 'LABEL' ? 'is-on' : ''}`} onClick={() => void saveSettings({ ...settings, format: 'LABEL' })}>Štítková tlačiareň (1 cenovka = 1 strana)</button>
        </div>
      </div>
      <div className="pt-settings-group">
        <h3>Vzhľad textu</h3>
        <label className="pt-scale">Názov <strong>{settings.nameScale}%</strong>
          <input type="range" min={70} max={200} step={5} value={settings.nameScale} onChange={e => void saveSettings({ ...settings, nameScale: Number(e.target.value) })} />
        </label>
        <label className="pt-scale">Cena <strong>{settings.priceScale}%</strong>
          <input type="range" min={70} max={250} step={5} value={settings.priceScale} onChange={e => void saveSettings({ ...settings, priceScale: Number(e.target.value) })} />
        </label>
        <label className="pt-scale">Všetko pod čiarou <strong>{settings.detailScale}%</strong>
          <input type="range" min={70} max={200} step={5} value={settings.detailScale} onChange={e => void saveSettings({ ...settings, detailScale: Number(e.target.value) })} />
        </label>
        <div className="pt-size-options">
          {ALIGN_OPTIONS.map(([key, label]) => <button key={key} type="button" className={`pt-size-btn ${settings.align === key ? 'is-on' : ''}`} onClick={() => void saveSettings({ ...settings, align: key })}>{label}</button>)}
        </div>
        <label className="pt-color">Farba ceny a pásika<input type="color" value={settings.color} onChange={e => void saveSettings({ ...settings, color: e.target.value })} /></label>
        <label className="pt-check"><input type="checkbox" checked={settings.showUnitLabel} onChange={e => void saveSettings({ ...settings, showUnitLabel: e.target.checked })} /> Zobraziť „/ {sample?.unit ?? 'ks'} s DPH“ pod cenou</label>
        <label className="pt-check"><input type="checkbox" checked={settings.nameBold} onChange={e => void saveSettings({ ...settings, nameBold: e.target.checked })} /> Tučnejšie písmo názvu</label>
      </div>
      {saving && <span className="pt-saving">Ukladám…</span>}
      {error && <p className="error-message">{error}</p>}
    </section>

    <section className="pt-body">
      <div className="pt-pick">
        <label className="pa-search"><span aria-hidden="true">⌕</span><input placeholder="Hľadať produkt – názov alebo PLU…" value={query} onChange={e => setQuery(e.target.value)} />{query && <button type="button" aria-label="Vymazať" onClick={() => setQuery('')}>×</button>}</label>
        <div className="pt-pick-controls">
          <select aria-label="Skupina" value={groupId} onChange={e => setGroupId(e.target.value)}>
            <option value="">Všetky skupiny</option>
            {topGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {groupId && <label className="pa-subs-toggle"><input type="checkbox" checked={includeSubs} onChange={toggleIncludeSubs} /> Vrátane podkategórií</label>}
          <select aria-label="Zoradenie" value={sort} onChange={e => setSort(e.target.value as Sort)}>
            <option value="name">Názov A → Z</option>
            <option value="price-asc">Cena od najnižšej</option>
            <option value="price-desc">Cena od najvyššej</option>
          </select>
          <div className="pa-view" role="group" aria-label="Zobrazenie">
            <button type="button" className={view === 'list' ? 'is-on' : ''} aria-pressed={view === 'list'} title="Zoznam" onClick={() => setView('list')}>☰</button>
            <button type="button" className={view === 'grid' ? 'is-on' : ''} aria-pressed={view === 'grid'} title="Kartičky" onClick={() => setView('grid')}>▦</button>
          </div>
        </div>
        {results.length > 0 && <label className="pt-select-all"><input type="checkbox" checked={allResultsSelected} onChange={toggleAllResults} /> {groupId ? 'Vybrať celú skupinu' : 'Vybrať všetko'} ({results.length})</label>}
        {!results.length && <p className="empty-message">Žiadny produkt nevyhovuje hľadaniu.</p>}
        {results.length > 0 && view === 'list' && <div className="pa-table-wrap"><table className="pa-table pt-pick-table">
          <thead><tr><th className="pa-col-check"><input type="checkbox" aria-label="Vybrať všetky zobrazené" checked={allResultsSelected} onChange={toggleAllResults} /></th><th>Produkt</th><th className="pa-col-price">Cena</th><th>Počet</th></tr></thead>
          <tbody>{results.map(p => {
            const n = counts[p.id] ?? 0
            return <tr key={p.id} className={n > 0 ? 'is-selected' : ''}>
              <td className="pa-col-check"><input type="checkbox" aria-label={`Vybrať ${p.name}`} checked={n > 0} onChange={() => setCount(p.id, n > 0 ? 0 : 1)} /></td>
              <td><div className="pa-product"><span className="pa-name"><strong>{p.name}</strong><small>{p.plu ? `PLU ${p.plu}` : '—'}</small></span></div></td>
              <td className="pa-col-price"><strong>{euro(p.priceCents)}</strong><small> / {p.unit}</small></td>
              <td className="pt-pick-action">{n > 0 && <div className="pt-stepper"><button type="button" onClick={() => setCount(p.id, n - 1)}>−</button><span>{n}</span><button type="button" onClick={() => setCount(p.id, n + 1)}>+</button></div>}</td>
            </tr>
          })}</tbody>
        </table></div>}
        {results.length > 0 && view === 'grid' && <div className="pt-pick-list">{results.map(p => {
          const n = counts[p.id] ?? 0
          return <label key={p.id} className={`pt-pick-row ${n > 0 ? 'is-picked' : ''}`}>
            <input type="checkbox" checked={n > 0} onChange={() => setCount(p.id, n > 0 ? 0 : 1)} />
            <div className="pt-pick-name"><strong>{p.name}</strong><small>{p.plu ? `PLU ${p.plu} · ` : ''}{euro(p.priceCents)} / {p.unit}</small></div>
            {n > 0 && <div className="pt-stepper" onClick={e => e.preventDefault()}><button type="button" onClick={() => setCount(p.id, n - 1)}>−</button><span>{n}</span><button type="button" onClick={() => setCount(p.id, n + 1)}>+</button></div>}
          </label>
        })}
        </div>}
      </div>

      <aside className="pt-preview">
        <h3>Vybrané ({totalTags})</h3>
        {!selectedIds.length && <p className="empty-message">Zatiaľ nič – pridajte produkty vľavo.</p>}
        {sample && <div className="pt-tag-sample">
          <div className="pt-tag-sample-card" style={{ alignItems: ALIGN_CSS[settings.align], ['--pt-accent' as string]: settings.color } as CSSProperties}>
            <strong className="pt-tag-sample-name" style={{ fontSize: `${15 * (settings.nameScale / 100)}px`, height: `${15 * (settings.nameScale / 100) * 1.15 * 3}px`, textAlign: settings.align, fontWeight: settings.nameBold ? 900 : 700 }}>{nbspOrphans(sample.name)}{sample.glutenFree && <span title="Bezlepkové"> 🌾</span>}{sample.lactoseFree && <span title="Bezlaktózové"> 🥛</span>}</strong>
            <span className="pt-tag-sample-price" style={{ fontSize: `${26 * (settings.priceScale / 100)}px`, textAlign: settings.align, color: settings.color }}>{euro(sample.priceCents)}{settings.showUnitLabel && <small>/ {sample.unit} s DPH</small>}</span>
            <div className="pt-tag-sample-rows" style={{ fontSize: `${12 * (settings.detailScale / 100)}px` }}>
              {settings.fields.includes('weight') && sample.weightGrams ? <div><span>hmot. (g)</span><strong>{sample.weightGrams}</strong></div> : null}
              {settings.fields.includes('weight') && !sample.weightGrams && sample.volumeMl ? <div><span>objem (ml)</span><strong>{sample.volumeMl}</strong></div> : null}
              {settings.fields.includes('unitPrice') && sampleUnitPrice && <div><span>jednot. cena {sampleUnitPrice.per}</span><strong>{euro(sampleUnitPrice.cents)}</strong></div>}
              {settings.fields.includes('allergens') && <div><span>Alergény</span><strong>{parseAllergens(sample.allergens).ids.join(', ') || '—'}</strong></div>}
            </div>
          </div>
          <small>Ukážka prvej cenovky</small>
        </div>}
        <div className="pt-actions">
          {selectedIds.length > 0 && <button type="button" className="pa-link" onClick={clearSelection}>Zrušiť výber</button>}
          <button type="button" className="primary-btn" disabled={!selectedIds.length} onClick={printTags}>🖨 Tlačiť cenovky</button>
        </div>

        <div className="pt-presets">
          <h3>Zostavy</h3>
          <div className="pt-preset-save">
            <input placeholder="Názov zostavy…" value={presetName} onChange={e => setPresetName(e.target.value)} disabled={presetBusy} />
            <button type="button" className="pa-ghost" disabled={presetBusy || !presetName.trim() || !selectedIds.length} onClick={() => void savePreset()}>💾 Uložiť výber</button>
          </div>
          {presetError && <p className="error-message">{presetError}</p>}
          {!presets.length && <p className="empty-message">Zatiaľ žiadne uložené zostavy.</p>}
          {presets.length > 0 && <ul className="pt-preset-list">{presets.map(p => <li key={p.id}>
            <span>{p.name}<small>{p.items.length} {p.items.length === 1 ? 'produkt' : p.items.length < 5 ? 'produkty' : 'produktov'}</small></span>
            <button type="button" className="pa-link" disabled={presetBusy} onClick={() => loadPreset(p)}>Načítať</button>
            <button type="button" className="icon-btn" aria-label={`Zmazať zostavu ${p.name}`} disabled={presetBusy} onClick={() => void deletePreset(p.id)}>×</button>
          </li>)}</ul>}
        </div>

        {selectedIds.length > 0 && <>
          <h3 className="pt-selected-heading">Vybrané produkty</h3>
          <ul className="pt-selected-list">{selectedIds.map(id => {
            const p = products.find(x => x.id === id)!
            return <li key={id}><span>{counts[id]}× {p.name}</span><button type="button" className="icon-btn" aria-label={`Odobrať ${p.name}`} onClick={() => setCount(id, 0)}>×</button></li>
          })}</ul>
        </>}
      </aside>
    </section>
  </div>
}
